<template>
    <details-drawer ref="drawer" :title="$t('client.detailsTitle')" :deleting="deleting"
                    @edit="editClient" @delete="deleteClient" @closed="$emit('closed')">
        <section class="details-drawer-identity">
            <h2>{{client.name}}</h2>
            <div class="details-drawer-balance">
                <span>{{$t('client.columnBalanceDue')}}</span>
                <span>{{money(client.totalBalanceDue)}}</span>
            </div>
        </section>

        <section class="details-drawer-section" v-loading="clientLoading">
            <div v-if="clientError" class="details-drawer-error" role="alert">
                <span>{{$t('client.detailsLoadFailed')}}</span>
                <button type="button" @click="loadClient">{{$t('client.retry')}}</button>
            </div>
            <template v-else-if="clientDetails">
                <h3>{{$t('client.contactDetails')}}</h3>
                <dl class="details-drawer-fields">
                    <div v-if="clientDetails.clientEmail">
                        <dt>{{$t('client.columnEmail')}}</dt>
                        <dd>{{clientDetails.clientEmail}}</dd>
                    </div>
                    <div v-if="clientDetails.phone">
                        <dt>{{$t('client.columnPhone')}}</dt>
                        <dd>{{clientDetails.phone}}</dd>
                    </div>
                    <div v-if="billingAddress">
                        <dt>{{$t('clientCreate.billingAddress')}}</dt>
                        <dd class="details-drawer-multiline">{{billingAddress}}</dd>
                    </div>
                    <div v-if="shippingAddress">
                        <dt>{{$t('clientCreate.shippingAddress')}}</dt>
                        <dd class="details-drawer-multiline">{{shippingAddress}}</dd>
                    </div>
                    <div v-if="clientDetails.itemDiscount != null">
                        <dt>{{$t('clientCreate.defaultDiscount')}}</dt>
                        <dd>{{discountText}}</dd>
                    </div>
                    <div v-if="clientDetails.note">
                        <dt>{{$t('clientCreate.labelNotes')}}</dt>
                        <dd class="details-drawer-multiline">{{clientDetails.note}}</dd>
                    </div>
                </dl>
            </template>
        </section>

        <section class="details-drawer-section details-history-section">
            <div class="details-history-heading">
                <h3>{{$t('client.invoiceHistory')}}</h3>
                <span v-if="!invoicesLoading && !invoicesError">{{invoiceTotal}}</span>
            </div>
            <div class="details-history-content" v-loading="invoicesLoading">
                <div v-if="invoicesError" class="details-drawer-error" role="alert">
                    <span>{{$t('client.invoicesLoadFailed')}}</span>
                    <button type="button" @click="loadInvoices(invoicePage)">{{$t('client.retry')}}</button>
                </div>
                <div v-else-if="!invoicesLoading && invoices.length === 0" class="details-history-empty">
                    <i class="el-icon-document" aria-hidden="true"></i>
                    <span>{{$t('client.noInvoices')}}</span>
                </div>
                <router-link v-for="invoice in invoices" :key="invoice.innerUUID" class="details-history-invoice"
                             :to="{path: '/invoiceUpdate', query: {innerUUID: invoice.innerUUID}}">
                    <div class="details-history-document">
                        <span class="details-history-number">{{invoice.numPrefix}}{{invoice.num}}</span>
                        <div class="details-history-meta">
                            <span>{{formatDate(invoice.invoiceStartDate * 1000)}}</span>
                            <span v-if="invoice.paidStatus === 2" class="list-status-badge list-status-green">{{$t('invoice.statusFullyPaid')}}</span>
                            <span v-else-if="invoice.balanceDueMoney > 0 && invoice.dueDate > 0 && invoice.dueDate * 1000 < Date.now()"
                                  class="list-status-badge list-status-red">{{$t('invoice.statusOverdue')}}</span>
                            <span v-else-if="invoice.paidStatus === 1" class="list-status-badge list-status-orange">{{$t('invoice.statusPartial')}}</span>
                            <span v-else class="list-status-badge list-status-blue">{{$t('invoice.filterUnpaid')}}</span>
                        </div>
                    </div>
                    <div class="details-history-amounts">
                        <span>{{money(invoice.totalMoney)}}</span>
                        <small>{{$t('client.columnBalanceDue')}}: {{money(invoice.balanceDueMoney)}}</small>
                    </div>
                    <i class="el-icon-arrow-right" aria-hidden="true"></i>
                </router-link>
            </div>
            <el-pagination v-if="invoiceTotal > invoicePageSize && !invoicesError" class="details-history-pagination"
                           small layout="prev, pager, next" :current-page="invoicePage"
                           :page-size="invoicePageSize" :total="invoiceTotal" :disabled="invoicesLoading"
                           @current-change="loadInvoices"></el-pagination>
        </section>
    </details-drawer>
</template>

<script>
    import DetailsDrawer from '@/components/common/DetailsDrawer'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {formatEnglishDate} from '@/utils/time'
    import {getBrowserTimeZone} from '@/utils/browserTimeZone'

    export default {
        name: 'ClientDetailsDrawer',
        components: {DetailsDrawer},
        props: {
            client: {type: Object, required: true},
            companyUuid: {type: String, required: true},
            setting: {type: Object, required: true}
        },
        data() {
            return {
                clientDetails: null,
                clientLoading: true,
                clientError: false,
                deleting: false,
                invoices: [],
                invoicesLoading: true,
                invoicesError: false,
                invoiceTotal: 0,
                invoicePage: 1,
                invoicePageSize: 20
            };
        },
        computed: {
            billingAddress() {
                return [this.clientDetails.billingAddressLine1, this.clientDetails.billingAddressLine2,
                    this.clientDetails.billingAddressLine3].filter(Boolean).join('\n');
            },
            shippingAddress() {
                return [this.clientDetails.shippingName, this.clientDetails.shippingAddressLine1,
                    this.clientDetails.shippingAddressLine2, this.clientDetails.shippingAddressLine3].filter(Boolean).join('\n');
            },
            discountText() {
                return Number(this.clientDetails.itemDiscountAsPercent) === 1
                    ? this.$NP.times(this.clientDetails.itemDiscount, 100) + '%'
                    : this.money(this.clientDetails.itemDiscount);
            }
        },
        methods: {
            money(value) {
                return xiaoshudianfenge(value, this.setting);
            },
            formatDate: formatEnglishDate,
            async loadClient() {
                this.clientLoading = true;
                this.clientError = false;
                try {
                    const response = await this.$axios.post('query_user_client', {
                        params: {innerUUID: this.client.innerUUID, companyUUID: this.companyUuid}
                    });
                    if (response.data.errorCode !== 0) {
                        throw new Error('Client query failed');
                    }
                    this.clientDetails = response.data.objectData;
                } catch {
                    this.clientError = true;
                } finally {
                    this.clientLoading = false;
                }
            },
            async loadInvoices(page) {
                this.invoicePage = page;
                this.invoicesLoading = true;
                this.invoicesError = false;
                this.invoices = [];
                try {
                    const response = await this.$axios.post('query_invoice_list_user_web', {
                        params: {
                            companyUUID: this.companyUuid,
                            clientUUID: this.client.innerUUID,
                            type: 0,
                            timePeriodType: 'ALL_TIME',
                            timeZone: getBrowserTimeZone(),
                            currentPage: page,
                            pageSize: this.invoicePageSize,
                            orderName: 'invoiceStartDate',
                            orderTpe: 2
                        }
                    });
                    if (response.data.errorCode !== 0) {
                        throw new Error('Client invoice history query failed');
                    }
                    this.invoices = response.data.dataList;
                    this.invoiceTotal = response.data.totalCount;
                } catch {
                    this.invoicesError = true;
                } finally {
                    this.invoicesLoading = false;
                }
            },
            editClient() {
                this.$router.push({path: '/clientUpdate', query: {innerUUID: this.client.innerUUID}});
            },
            async deleteClient() {
                try {
                    await this.$confirm(this.$t('client.deleteConfirm'), this.$t('common.delete'), {
                        confirmButtonText: this.$t('common.delete'),
                        cancelButtonText: this.$t('common.cancel'),
                        type: 'warning'
                    });
                } catch {
                    return;
                }
                this.deleting = true;
                try {
                    const response = await this.$axios.post('web_batch_action', {
                        params: {
                            entityType: 'CLIENT',
                            action: 'DELETE',
                            companyUUID: this.companyUuid,
                            innerUUIDList: [this.client.innerUUID]
                        }
                    });
                    if (response.data.errorCode !== 0) {
                        throw new Error('Client deletion failed');
                    }
                    this.$message.success(this.$t('client.deleted'));
                    this.$refs.drawer.close();
                    this.$emit('deleted', response.data.processedCount);
                } catch {
                    this.$message.error(this.$t('common.deleteFailed'));
                } finally {
                    this.deleting = false;
                }
            }
        },
        mounted() {
            this.loadClient();
            this.loadInvoices(1);
        }
    }
</script>
